import { IToaster } from "@blueprintjs/core";
import {
    JitsiConference,
    JitsiConnection,
    JitsiLocalTrack,
    JitsiMediaDeviceInfo,
    JitsiTrack,
} from "lib/lib-jitsi-meet";
import { IObservableArray, observable } from "mobx";
import Configuration from "./configuration";
import { getDefaultUserSettings } from "./settings";

export interface JitsiManagerState {
    roomName?: string;
    connection?: JitsiConnection;
    room?: JitsiConference;
    isJitsiModuleLoaded: boolean;
    hasJitsiInitialized: boolean;
    isReconnecting: boolean;
}

export function getDefaultJitsiManagerState(): JitsiManagerState {
    return {
        isJitsiModuleLoaded: false,
        hasJitsiInitialized: false,
        isReconnecting: false,
    };
}

export type AppToaster = IToaster;

export type ChatRequestStatus =
    | "incomingRequest" // other user initiated request
    | "outgoingRequest" // we initiated request
    | "incomingAccept" // after we request, accepted by other user
    | "outgoingAccept" // other user requested, we accepted
    | "incomingBlock" // other user blocked us
    | "outgoingBlock"; // we blocked other user

export interface RemoteVideoLayoutOptions {
    userId: string;
    isPinned: boolean;
    isDropdownOpen: boolean;

    /* used with react-grid-layout */
    gridWidth?: number;
    gridHeight?: number;
    gridX?: number;
    gridY?: number;

    /* @deprecated */
    isFocused: boolean;
}

// when the app is first loaded onto the page, we read this setting from local storage once.
// need to store it to module global variable because getDefault...() is called multiple times.
let storedVideoLayout: RemoteVideoLayoutOptions[] | undefined;
let triedLoadingVideoLayout = false;

export function getDefaultRemoteVideoLayoutOptions(roomName: string, userId: string): RemoteVideoLayoutOptions {
    if (!triedLoadingVideoLayout) {
        storedVideoLayout = getDefaultUserSettings(roomName).videoLayout;
        triedLoadingVideoLayout = true;
    }

    const storedLayout = storedVideoLayout?.find((layout) => layout.userId === userId);
    return (
        storedLayout ?? {
            userId,
            isPinned: false,
            isFocused: false,
            isDropdownOpen: false,
        }
    );
}

export interface RoomState {
    isHost: boolean;
    roomJoined: "none" | "joining" | "joined" | "left";

    tracks: Map<string, JitsiTrack[]>;
    spotlightUserId?: string | null;
    // use IObservable to get types for additional methods
    videoLayout: IObservableArray<RemoteVideoLayoutOptions>;
    videoOrientation: "landscape" | "portrait";
    /**
     * Local map of changed display names to force updates to remote video grid when
     * we receive the DISPLAY_NAME_CHANGED event in the conference.
     */
    displayNames: Map<string, string>;
    privateChatRequests: Map<string, ChatRequestStatus>;

    // for performance only
    backgroundState: "default" | "dark";

    /** Debugging only */
    videoFixtures: string[];
    enableDanceFloorBackground: boolean;
}

// expect return value to be wrapped in observable()
export function getDefaultRoomState(options: { isHost?: boolean } = {}): RoomState {
    const { isHost = false } = options;
    return {
        isHost,
        roomJoined: "none",
        tracks: new Map(),
        videoLayout: observable([]),
        videoOrientation: "landscape",
        displayNames: new Map(),
        privateChatRequests: new Map(),
        backgroundState: "default",
        videoFixtures: observable([]),
        enableDanceFloorBackground: Configuration.danceFloorBackground.ENABLED_BY_DEFAULT,
    };
}

export interface PerformanceState {
    // map grid id -> user id
    userLayout: Map<number, string>;
    scene: number;
    scenePrompts: Map<
        number,
        {
            prompt: string;
            // undefined means keep playing old track, null means stop
            music?: string | null;
            stageBackground?: "disco" | "wavy";
            advanceWithTimeout?: number;
            advanceWithMentions?: "all" | "any" | "duo";
        }
    >;
    sceneDuos?: [[string, string], [string, string]];
    sceneMentionedUsers: Map<number, Set<string>>;
}

export function getDefaultPerformanceState(): PerformanceState {
    return {
        userLayout: new Map(),
        scene: 0,
        sceneMentionedUsers: new Map(),
        scenePrompts: new Map([
            [
                0,
                {
                    prompt: "Cyberdance is a story. Can you help figure out what it is?",
                    music: null,
                },
            ],
            [
                1,
                {
                    prompt:
                        "Are you enjoying the live footage? Can you get the dancers' attention so that they can go to the party?",
                    music: "club_scene_music.mp3",
                    advanceWithMentions: "all",
                },
            ],
            [
                2,
                {
                    prompt: "Success! Time to dance :)",
                    stageBackground: "wavy",
                    advanceWithTimeout: 15000,
                },
            ],
            [
                3,
                {
                    prompt: "Your turn to play matchmaker. Pick a duo to dance together.",
                    advanceWithMentions: "duo",
                    stageBackground: "wavy",
                },
            ],
            [
                4,
                {
                    prompt: "Now cheer for your favorite duo. Keep the dance going!",
                    music: "duo_scene_music.mp3",
                    stageBackground: "disco",
                    advanceWithTimeout: 30000,
                },
            ],
            [
                5,
                {
                    prompt: "Are you feeling entertained?",
                    stageBackground: "disco",
                    advanceWithTimeout: 30000,
                },
            ],
            [
                6,
                {
                    prompt: "Do you see yourself in any of the dancers?",
                    advanceWithMentions: "all",
                    stageBackground: "disco",
                },
            ],
            [
                7,
                {
                    prompt: "How does it feel to be watched?",
                    music: null,
                },
            ],
        ]),
    };
}

export interface AudioMeterState {
    level: number;
    isClipping: boolean;
}

/**
 * Current user settings and info
 */
export interface LocalUserState {
    readonly userId: string;
    displayName?: string;
    avatar?: string;
    audioOutputLevel: number;
    isSelfVideoDataLoaded: boolean;
    audioInputMeter: AudioMeterState;
    videoEffect?: "jitsiBlur" | "virtualBackground";
    virtualBackground: {
        isDialogOpen: boolean;
        selectedImage?: string;
    };
    isBottomPanelCollapsed: boolean;

    // for performance views
    readonly performerId?: number;
}

export function getDefaultLocalUserState(
    userId: string,
    defaults: {
        displayName?: string;
        performerId?: number;
    } = {}
): LocalUserState {
    const { displayName = getDefaultUserSettings().displayName, performerId } = defaults;
    return {
        userId,
        displayName,
        audioOutputLevel: 0.5,
        isSelfVideoDataLoaded: false,
        audioInputMeter: {
            level: 0,
            isClipping: false,
        },
        virtualBackground: {
            isDialogOpen: false,
        },
        isBottomPanelCollapsed: false,
        performerId,
    };
}

/**
 * State for dealing with local tracks
 */
export interface MediaDevicesState {
    audioInputs: JitsiMediaDeviceInfo[];
    videoInputs: JitsiMediaDeviceInfo[];
    audioOutputs: JitsiMediaDeviceInfo[];

    audioInputDeviceId: string;
    videoInputDeviceId: string;
    audioOutputDeviceId: string;

    audioInputTrack?: JitsiLocalTrack;
    videoInputTrack?: JitsiLocalTrack;

    // true if we are getting permissions for the first time in this client.
    // we must re-request devices to get their display names displayed correctly
    shouldReEnumerateDevices: boolean;
}

export function getDefaultMediaDevicesState(): MediaDevicesState {
    const { audioInputDeviceId, videoInputDeviceId, audioOutputDeviceId } = getDefaultUserSettings();

    return {
        audioInputs: [],
        videoInputs: [],
        audioOutputs: [],
        audioInputDeviceId: audioInputDeviceId!,
        videoInputDeviceId: videoInputDeviceId!,
        audioOutputDeviceId: audioOutputDeviceId!,
        shouldReEnumerateDevices: false,
    };
}

export interface ChatMessage {
    userId: string;
    message: string;
    timestamp: Date;
    userNickname: string;
    isSelf: boolean;
}

export interface ChatRoomState {
    public: ChatUIState;
    private: Map<string, ChatUIState>;
}

export interface ChatUIState {
    messages: IObservableArray<ChatMessage>;
    inputValue: string;
    isMessageInFlight: boolean;
    /** true if we think the user has scrolled up to look at recent messages */
    isUserScrolledUp: boolean;
}

export function getDefaultChatUIState(messages: ChatMessage[] = []): ChatUIState {
    return {
        messages: observable(messages),
        inputValue: "",
        isMessageInFlight: false,
        isUserScrolledUp: false,
    };
}

export function getDefaultChatRoomState(): ChatRoomState {
    return {
        public: getDefaultChatUIState(),
        private: new Map(),
    };
}
